<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import Button from 'primevue/button'
import InputNumber from 'primevue/inputnumber'
import Slider from 'primevue/slider'
import GpxRecentValues from './GpxRecentValues.vue'
import {
  clamp,
  displayToMeters,
  formatMetresHintLine,
  metersToDisplay,
  METERS_PER_MILE,
  roundDisplayUnit,
  roundMetersToStep,
  type DistanceInputUnit,
  unitShortLabel,
} from '../utils/distance-units'
import { useRecentValues, type RecentListItem } from '../composables/useRecentValues'

/**
 * Distance entry with slider + numeric input. **`inputUnit` defaults to `miles`** (primary use case); set `km` or `m` for metric-only screens.
 * Emitted `v-model` is **whole metres** (integers). With **`storageKey`**, the **most recent** distance (first in Recent) is applied **once on mount** by default (overwrites placeholder defaults like `ref(200)`). Set **`preserveParentModelOnMount`** when the parent already bound a **saved entity** value that must not be replaced. Requires `app.directive('tooltip', Tooltip)` from `primevue/tooltip` for the info icon.
 * **`size`:** same values as PrimeVue `Button` / `InputNumber` — `small` (denser row, narrow input), `large` (more spacing, wider input); omit for default.
 */
const props = withDefaults(
  defineProps<{
    modelValue: number | null
    /** Slider/input display unit. Default **`miles`**. */
    inputUnit?: DistanceInputUnit
    minMeters?: number
    maxMeters?: number
    stepMeters?: number
    title?: string
    tooltip?: string
    storageKey?: string
    recentLabel?: string
    maxRecentItems?: number
    disabled?: boolean
    /** PrimeVue-aligned: `small` | `large`; default = normal (no size). */
    size?: 'small' | 'large'
    /**
     * When `true`, **do not** apply Recent on mount if `modelValue` is already a finite number (loaded entity).
     * Default `false` — Recent wins on mount so placeholder refs are replaced by the last saved distance.
     */
    preserveParentModelOnMount?: boolean
    /** Invalid state (red border on the numeric input). */
    invalid?: boolean
  }>(),
  {
    minMeters: 0.05 * METERS_PER_MILE,
    maxMeters: 5 * METERS_PER_MILE,
    stepMeters: 0.05 * METERS_PER_MILE,
    title: 'Distance',
    tooltip: undefined,
    storageKey: undefined,
    recentLabel: 'Recent',
    maxRecentItems: 5,
    disabled: false,
    inputUnit: 'miles',
    preserveParentModelOnMount: false,
    invalid: false,
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: number | null]
  /** Fired when the distance slider handle is released (PrimeVue `slideend`). */
  sliderSlideEnd: []
}>()

const resolvedUnit = computed<DistanceInputUnit>(() => props.inputUnit)

const hintMode = computed<'alongsideImperialInput' | 'alongsideMetricInput'>(() =>
  resolvedUnit.value === 'miles' ? 'alongsideImperialInput' : 'alongsideMetricInput'
)

const minDisplay = computed(() =>
  roundDisplayUnit(metersToDisplay(props.minMeters, resolvedUnit.value), resolvedUnit.value)
)
const maxDisplay = computed(() =>
  roundDisplayUnit(metersToDisplay(props.maxMeters, resolvedUnit.value), resolvedUnit.value)
)
const stepDisplay = computed(() => {
  const u = resolvedUnit.value
  let raw: number
  if (u === 'miles') raw = props.stepMeters / METERS_PER_MILE
  else if (u === 'km') raw = props.stepMeters / 1000
  else raw = props.stepMeters
  return roundDisplayUnit(raw, u)
})

const currentMeters = computed(() => props.modelValue ?? props.minMeters)

const displayValue = computed(() =>
  roundDisplayUnit(metersToDisplay(currentMeters.value, resolvedUnit.value), resolvedUnit.value)
)

const fractionDigits = computed(() => (resolvedUnit.value === 'm' ? 0 : 2))

const rootStackClass = computed(() => {
  if (props.size === 'small') return 'space-y-1.5'
  if (props.size === 'large') return 'space-y-3'
  return 'space-y-2'
})

const sliderRowClass = computed(() => {
  if (props.size === 'small') return 'flex flex-col gap-2 sm:flex-row sm:items-center'
  if (props.size === 'large') return 'flex flex-col gap-4 sm:flex-row sm:items-center'
  return 'flex flex-col gap-3 sm:flex-row sm:items-center'
})

const inputNumberInputClass = computed(() => {
  const base = 'text-center tabular-nums'
  if (props.size === 'small') return `${base} w-16 max-w-[4rem]`
  if (props.size === 'large') return `${base} w-[8rem] min-w-[7rem]`
  return `${base} w-[6.5rem]`
})

const hintLineClass = computed(() => {
  if (props.size === 'small') return 'text-[10px] text-gpx_gray-300'
  if (props.size === 'large') return 'text-xs text-gpx_gray-300'
  return 'text-[11px] text-gpx_gray-300'
})

const infoIconClass = computed(() => {
  if (props.size === 'small') return 'text-xs'
  if (props.size === 'large') return 'text-base'
  return 'text-sm'
})

const unitSuffixClass = computed(() => {
  if (props.size === 'small') return 'text-[11px]'
  if (props.size === 'large') return 'text-sm'
  return 'text-xs'
})

const metresHintLine = computed(() =>
  props.modelValue != null ? formatMetresHintLine(props.modelValue, hintMode.value) : ''
)

const { items: recentItems, record: recordRecent, remove: removeRecent } = useRecentValues(
  () => props.storageKey ?? '',
  { maxItems: props.maxRecentItems }
)

function formatDistanceChip(value: RecentListItem): string {
  const m = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(m)) return String(value)
  const u = resolvedUnit.value
  const v = roundDisplayUnit(metersToDisplay(m, u), u)
  const digits = u === 'm' ? 0 : 2
  const s = digits === 0 ? String(Math.round(v)) : v.toFixed(digits)
  return `${s} ${unitShortLabel(u)}`
}

let recentDebounce: ReturnType<typeof setTimeout> | null = null

function commitMeters(meters: number): void {
  const stepped = roundMetersToStep(meters, props.stepMeters)
  const clamped = clamp(stepped, props.minMeters, props.maxMeters)
  const asWholeMetres = Math.round(clamped)
  emit('update:modelValue', asWholeMetres)
}

function scheduleRecentRecord(): void {
  if (!props.storageKey) return
  if (recentDebounce) clearTimeout(recentDebounce)
  recentDebounce = setTimeout(() => {
    recentDebounce = null
    const m = props.modelValue ?? props.minMeters
    recordRecent(Math.round(m))
  }, 450)
}

function onDisplayChange(v: number): void {
  const rounded = roundDisplayUnit(v, resolvedUnit.value)
  const m = displayToMeters(rounded, resolvedUnit.value)
  commitMeters(m)
  scheduleRecentRecord()
}

function onSliderModel(v: number | number[]): void {
  const n = Array.isArray(v) ? v[0] : v
  if (typeof n === 'number' && Number.isFinite(n)) {
    onDisplayChange(n)
  }
}

function onInputNumberUpdate(n: number | null): void {
  if (n != null && Number.isFinite(n)) {
    onDisplayChange(n)
  }
}

function onInputBlur(): void {
  if (recentDebounce) {
    clearTimeout(recentDebounce)
    recentDebounce = null
  }
  if (props.storageKey) {
    recordRecent(Math.round(props.modelValue ?? props.minMeters))
  }
}

function onSelectRecent(value: RecentListItem): void {
  const m = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(m)) return
  commitMeters(m)
  if (props.storageKey) {
    recordRecent(Math.round(m))
  }
}

function onRemoveRecent(value: RecentListItem): void {
  removeRecent(value)
}

const inputNumberKey = ref(0)
watch(resolvedUnit, () => {
  inputNumberKey.value += 1
})

onMounted(() => {
  if (!props.storageKey || props.disabled || recentItems.value.length === 0) return
  if (
    props.preserveParentModelOnMount &&
    props.modelValue != null &&
    Number.isFinite(props.modelValue)
  ) {
    return
  }
  const raw = recentItems.value[0]
  const m = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(m)) return
  const stepped = roundMetersToStep(m, props.stepMeters)
  const clamped = clamp(stepped, props.minMeters, props.maxMeters)
  emit('update:modelValue', Math.round(clamped))
})
</script>

<template>
  <div :class="rootStackClass">
    <div class="flex items-center gap-1.5">
      <label class="text-xs font-medium text-gpx_gray-100">{{ title }}</label>
      <Button
        v-if="tooltip"
        v-tooltip.top="tooltip"
        type="button"
        text
        rounded
        severity="secondary"
        :size="size"
        class="!p-0.5 !text-gpx_gray-300 hover:!text-gpx_gray-100"
        :aria-label="tooltip"
      >
        <span class="pi pi-info-circle" :class="infoIconClass" />
      </Button>
    </div>

    <div :class="sliderRowClass">
      <div class="min-w-0 flex-1">
        <Slider
          class="w-full"
          :model-value="displayValue"
          :min="minDisplay"
          :max="maxDisplay"
          :step="stepDisplay"
          :disabled="disabled"
          @update:model-value="onSliderModel"
          @slideend="emit('sliderSlideEnd')"
        />
      </div>
      <div class="flex items-center gap-1.5">
        <InputNumber
          :key="inputNumberKey"
          :model-value="displayValue"
          :min="minDisplay"
          :max="maxDisplay"
          :step="stepDisplay"
          :disabled="disabled"
          :invalid="invalid"
          :size="size"
          :min-fraction-digits="fractionDigits"
          :max-fraction-digits="fractionDigits"
          :input-class="inputNumberInputClass"
          @update:model-value="onInputNumberUpdate"
          @blur="onInputBlur"
        />
        <span class="text-gpx_gray-200 whitespace-nowrap" :class="unitSuffixClass">{{
          resolvedUnit === 'miles' ? 'mi' : resolvedUnit === 'km' ? 'km' : 'm'
        }}</span>
      </div>
    </div>

    <p v-if="modelValue != null && metresHintLine" :class="hintLineClass">
      &approx; {{ metresHintLine }}
    </p>

    <GpxRecentValues
      v-if="storageKey"
      :items="recentItems"
      :label="recentLabel"
      :current-value="modelValue"
      :format-label="formatDistanceChip"
      @select="onSelectRecent"
      @remove="onRemoveRecent"
    />
  </div>
</template>
